import { useState, useEffect, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import { Card, CardBody, CardHeader, Row, Col, Input, Label, Spinner } from 'reactstrap';
import { H5 } from '../../../AbstractElements';
import LegalConsultationService from '../../../Api/services/legal-consultation.service';
import { useAuth } from '../../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { components } from '../../../Api/types/openapi';
import type { EventInput } from '@fullcalendar/core';

type LegalConsulationResponse = components['schemas']['LegalConsulationResponse'];
type LegalConsulationForClientResponse = components['schemas']['LegalConsulationForClientResponse'];
type LegalConsulationForLawyerResponse = components['schemas']['LegalConsulationForLawyerResponse'];

type ConsultationData = LegalConsulationResponse | LegalConsulationForClientResponse | LegalConsulationForLawyerResponse;

const LegalConsultationsCalendar = () => {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [consultations, setConsultations] = useState<ConsultationData[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewFilter, setViewFilter] = useState<'all' | 'P' | 'C' | 'R' | 'S'>('all');
  const [lawyerFilter, setLawyerFilter] = useState<number | null>(null);

  useEffect(() => {
    loadConsultations();
  }, [viewFilter, lawyerFilter]);

  const loadConsultations = async () => {
    try {
      setLoading(true);
      const params: any = {
        limit: 1000, // Obtener todas las consultas para el calendario
      };

      if (viewFilter !== 'all') {
        params.status = viewFilter;
      }

      if (lawyerFilter) {
        params.lawyer = lawyerFilter;
      }

      let data: any;
      
      if (hasRole('admin')) {
        data = await LegalConsultationService.getAdminConsultations(params);
      } else if (hasRole('lawyer')) {
        data = await LegalConsultationService.getLawyerConsultations(params);
      } else if (hasRole('client')) {
        data = await LegalConsultationService.getClientConsultations(params);
      } else {
        return;
      }

      setConsultations((data.results || []) as ConsultationData[]);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Error al cargar consultas';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Convertir consultas a eventos del calendario
  const calendarEvents = useMemo<EventInput[]>(() => {
    const events: EventInput[] = [];
    
    consultations
      .filter((consultation) => {
        // Filtrar solo consultas con fecha y hora válidas
        return consultation.consultation_date && 
               consultation.init_consultation_time && 
               consultation.end_consultation_time;
      })
      .forEach((consultation) => {
        const date = consultation.consultation_date!;
        const startTime = consultation.init_consultation_time!;
        const endTime = consultation.end_consultation_time!;

        // Combinar fecha y hora para crear objetos Date
        const startDateTime = new Date(`${date}T${startTime}`);
        const endDateTime = new Date(`${date}T${endTime}`);

        // VALIDAR que las fechas sean válidas
        if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
          console.warn(`Fecha/hora inválida para consulta ${consultation.id}:`, {
            date,
            startTime,
            endTime
          });
          return; // Saltar eventos con fechas inválidas
        }

        // Determinar color según estado
        let backgroundColor = '#3788d8'; // Azul por defecto
        let borderColor = '#3788d8';
        
        switch (consultation.status) {
          case 'P':
            backgroundColor = '#ffc107'; // Amarillo - Pendiente
            borderColor = '#ffc107';
            break;
          case 'C':
            backgroundColor = '#dc3545'; // Rojo - Cancelada
            borderColor = '#dc3545';
            break;
          case 'R':
            backgroundColor = '#17a2b8'; // Azul claro - Reagendada
            borderColor = '#17a2b8';
            break;
          case 'S':
            backgroundColor = '#28a745'; // Verde - Realizada
            borderColor = '#28a745';
            break;
        }

        // Título del evento
        const clientName = (consultation as any).client?.name || (consultation as any).client?.email || 'Sin cliente';
        const lawyerName = (consultation as any).lawyer?.name || (consultation as any).lawyer?.email || 'Sin asignar';
        const title = hasRole('admin') 
          ? `${clientName} - ${lawyerName}`
          : hasRole('lawyer')
          ? clientName
          : lawyerName;

        events.push({
          id: consultation.id?.toString() || '',
          title: title,
          start: startDateTime,
          end: endDateTime,
          backgroundColor,
          borderColor,
          extendedProps: {
            consultation: consultation,
          },
        });
      });
    
    return events;
  }, [consultations, hasRole]);

  const handleEventClick = (clickInfo: any) => {
    const consultationId = clickInfo.event.id;
    if (consultationId) {
      navigate(`${process.env.PUBLIC_URL}/legal-consultations/${consultationId}`);
    }
  };

  const handleDateClick = (dateClickInfo: any) => {
    // Opcional: navegar a crear consulta con fecha pre-seleccionada
    const selectedDate = dateClickInfo.dateStr;
    navigate(`${process.env.PUBLIC_URL}/legal-consultations/create?date=${selectedDate}`);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div style={{ marginTop: '20px' }}>
      {/* Filtros */}
      <Row className="mb-3">
        <Col md="3">
          <Label for="status-filter" className="form-label">
            <strong>Filtrar por estado:</strong>
          </Label>
          <Input
            type="select"
            id="status-filter"
            value={viewFilter}
            onChange={(e) => setViewFilter(e.target.value as any)}
          >
            <option value="all">Todas las consultas</option>
            <option value="P">Pendientes</option>
            <option value="C">Canceladas</option>
            <option value="R">Reagendadas</option>
            <option value="S">Realizadas</option>
          </Input>
        </Col>
        <Col md="9">
          {/* Leyenda de colores */}
          <div className="d-flex align-items-end h-100 gap-3">
            <small className="text-muted mb-2">
              <strong>Leyenda:</strong>
            </small>
            <div className="d-flex gap-2 flex-wrap">
              <div className="d-flex align-items-center gap-1">
                <div style={{ width: '16px', height: '16px', backgroundColor: '#ffc107', borderRadius: '3px' }}></div>
                <small>Pendiente</small>
              </div>
              <div className="d-flex align-items-center gap-1">
                <div style={{ width: '16px', height: '16px', backgroundColor: '#dc3545', borderRadius: '3px' }}></div>
                <small>Cancelada</small>
              </div>
              <div className="d-flex align-items-center gap-1">
                <div style={{ width: '16px', height: '16px', backgroundColor: '#17a2b8', borderRadius: '3px' }}></div>
                <small>Reagendada</small>
              </div>
              <div className="d-flex align-items-center gap-1">
                <div style={{ width: '16px', height: '16px', backgroundColor: '#28a745', borderRadius: '3px' }}></div>
                <small>Realizada</small>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Calendario */}
      <Card className="shadow-sm">
        <CardBody style={{ padding: '20px' }}>
          <div className="demo-app-calendar">
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay',
              }}
              events={calendarEvents}
              eventClick={handleEventClick}
              dateClick={handleDateClick}
              editable={false}
              selectable={true}
              selectMirror={true}
              dayMaxEvents={true}
              weekends={true}
              locale={esLocale}
              height="auto"
              eventDisplay="block"
              eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                meridiem: false,
              }}
              eventTextColor="#ffffff"
              eventBorderColor="transparent"
              dayHeaderFormat={{ weekday: 'long' }}
              buttonText={{
                today: 'Hoy',
                month: 'Mes',
                week: 'Semana',
                day: 'Día'
              }}
              moreLinkClick="popover"
              eventClassNames="fc-event-custom"
            />
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default LegalConsultationsCalendar;

